import React from "react";
import { Button, DataGrid, Toolbar } from "devextreme-react";
import { IColumnProps } from "devextreme-react/data-grid";
import { Item } from "devextreme-react/toolbar";
import { SelectionChangedEvent } from "devextreme/ui/data_grid";
import { alertError, confirm2, DataBool, notifySuccess, stdTimeFormat } from "../../base";
import { employeeTokenService } from "../../api";
import { EmployeeModel, EmployeeTokenModel } from "../../models";
import { createBoolColumn, createGridOptions, RefPopup, StdGrid, PopupContentBox } from "../../components";

interface EmployeeTokenPopupState {
    tokens: EmployeeTokenModel[];
    selectedTokens: string[];
}

export default class EmployeeTokenPopup extends React.Component<any, EmployeeTokenPopupState> {
    moduleName = "EmployeeToken";
    popupRef = React.createRef<any>();
    gridRef = React.createRef<DataGrid>();
    employee?: EmployeeModel;
    gridOptions = createGridOptions("employeeTokens", this.gridRef, {
        rowAlternationEnabled: true,
        columnAutoWidth: true,
        allowColumnResizing: true,
        columnResizingMode: "widget",
        filterRow: { visible: false },
        paging: { enabled: false },
        pager: { visible: false },
        groupPanel: { visible: false },
        columnChooser: { enabled: false },
        export: { enabled: false },
        selection: { mode: "multiple", showCheckBoxesMode: "always" }
    });

    columns: IColumnProps[] = [
        { dataField: "platformText", width: 120 },
        { dataField: "username", width: 100 },
        { dataField: "createIp", width: 120 },
        { dataField: "createdAt", dataType: "datetime", format: stdTimeFormat, width: 160 },
        { dataField: "latestLoginIp", width: 120 },
        { dataField: "latestLoginTime", dataType: "datetime", format: stdTimeFormat, width: 160 },
        { dataField: "absoluteExpireAt", dataType: "datetime", format: stdTimeFormat, width: 160 },
        { dataField: "inactiveExpireAt", dataType: "datetime", format: stdTimeFormat, width: 160 },
        createBoolColumn({ dataField: "expired", width: 80 })
    ];

    constructor(props: any) {
        super(props);

        this.state = {
            tokens: [],
            selectedTokens: []
        };

        this.gridOptions.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.deleteSelected = this.deleteSelected.bind(this);
        this.deleteAll = this.deleteAll.bind(this);
        this.clearExpired = this.clearExpired.bind(this);
        this.cancel = this.cancel.bind(this);
    }

    show(employee: EmployeeModel) {
        this.employee = employee;
        this.setState({ tokens: [], selectedTokens: [] });
        this.popupRef.current?.instance.show();
        this.loadTokens();
    }

    hide() {
        this.popupRef.current?.instance.hide();
    }

    async loadTokens() {
        if (!this.employee?.id) {
            return;
        }

        const tokens = await employeeTokenService.getByEmployeeId(this.employee.id);
        this.setState({ tokens: tokens || [], selectedTokens: [] });
        this.gridRef.current?.instance?.clearSelection();
    }

    onSelectionChanged(e: SelectionChangedEvent) {
        this.setState({
            selectedTokens: (e.selectedRowKeys || []) as string[]
        });
    }

    async deleteSelected() {
        const { selectedTokens } = this.state;
        if (!this.employee?.id || selectedTokens.length === 0) {
            return;
        }

        if (!await confirm2(`确定删除选定的 ${selectedTokens.length} 条长期登录票据吗？`)) {
            return;
        }

        const res = await employeeTokenService.delete(this.employee.id, selectedTokens);
        this.handleResult(res, "已删除选定票据");
    }

    async deleteAll() {
        if (!this.employee?.id || this.state.tokens.length === 0) {
            return;
        }

        if (!await confirm2("确定删除该员工的全部长期登录票据吗？")) {
            return;
        }

        const res = await employeeTokenService.deleteAll(this.employee.id);
        this.handleResult(res, "已删除全部票据");
    }

    async clearExpired() {
        if (!this.employee?.id) {
            return;
        }

        const expiredCount = this.state.tokens.filter(t => t.expired).length;
        if (expiredCount === 0) {
            notifySuccess("没有失效票据");
            return;
        }

        if (!await confirm2(`确定清除 ${expiredCount} 条失效票据吗？`)) {
            return;
        }

        const res = await employeeTokenService.clearExpired(this.employee.id);
        this.handleResult(res, "已清除失效票据");
    }

    handleResult(res: DataBool<number>, successText: string) {
        if (res.succeeded) {
            notifySuccess(`${successText}（${res.data || 0}）`);
            this.loadTokens();
        } else {
            alertError(res.message!);
        }
    }

    cancel() {
        this.hide();
    }

    render() {
        const { tokens, selectedTokens } = this.state;
        const expiredCount = tokens.filter(t => t.expired).length;

        return (
            <RefPopup
                popupRef={this.popupRef}
                baseOptions={{ className: "global-dialogs" }}
                width={1280}
                height={640}
                title={`长期登录票据 - ${this.employee?.name || ""}`}>
                <PopupContentBox
                    scrollable={false}
                    hideSavebutton={true}
                    onSave={() => { }}
                    onCancel={this.cancel}
                    cancelButtonText="关闭">
                    <Toolbar>
                        <Item location="before">
                            <Button
                                text="删除选定"
                                type="danger"
                                disabled={selectedTokens.length === 0}
                                onClick={this.deleteSelected} />
                        </Item>
                        <Item location="before">
                            <Button
                                text="删除所有"
                                type="danger"
                                disabled={tokens.length === 0}
                                onClick={this.deleteAll} />
                        </Item>
                        <Item location="before">
                            <Button
                                text="清除失效"
                                disabled={expiredCount === 0}
                                onClick={this.clearExpired} />
                        </Item>
                        <Item location="after">
                            <span className="text-muted">共 {tokens.length} 条，失效 {expiredCount} 条</span>
                        </Item>
                    </Toolbar>
                    <StdGrid
                        moduleName={this.moduleName}
                        gridRef={this.gridRef}
                        dataSource={tokens}
                        options={{
                            ...this.gridOptions,
                            keyExpr: "token",
                            height: 480
                        }}
                        columns={this.columns}
                        autoHeight={true} />
                </PopupContentBox>
            </RefPopup>
        );
    }
}
